import {
  CalculatePriceInterface,
  PhysicalCheckout,
  StoreImageEnum,
} from '@systemeio/digital-store-shared'
import { LanguageCodeEnum, PrimaryButton, cents } from '@systemeio/ui'
import ItemTabs, { ItemTabType } from 'modules/stores/store/components/item-tabs'
import ThankYouTab from 'modules/stores/store/components/tabs/thank-you-tab'
import { STORE_ITEM_CLASS_NAME } from 'modules/stores/store/constants/store-item-class-name'
import Thumbnail from 'modules/stores/store/content/product/components/thumbnail'
import { useStoreEditItemContext } from 'modules/stores/store/context/store-item-edit-context'
import { useStore } from 'modules/stores/store/hooks/use-store'
import { StoreId } from 'modules/stores/types/stores-interface'
import { FC, useEffect, useState } from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'
import { useProduct } from '../../../../../products/hooks/use-products'
import ItemActions from '../../../components/item-actions'
import { useStorePreviewContext } from '../../../context/store-preview-context'
import {
  PhysicalProductItemEditType,
  PhysicalProductItemErrorsType,
  PhysicalProductItemViewType,
} from '../types/product-interface'
import { PhysicalCheckout as Checkout } from './components/checkout'
import PhysicalProduct from './components/physical-product'
import { defaultErrors } from './constants/default-errors'
import { defaultValues } from './constants/default-values'
import { PhysicalProductTabEnum, tabLocoKeys } from './enum/physical-product-tab-enum'
import { useErrorMessageInTabs } from './hooks/use-error-message-in-tabs'
import { useSavePhysicalProductItem } from './hooks/use-save-physical-product-item'
import { getNextTab } from './utls/get-next-tab-physical-product'
import { useStoreContactFields } from 'modules/stores/store/hooks/use-store-contact-fields'
import { physicalProductInitFields } from './constants/mandatory-fields'
import { PhysicalProductInterface } from '@systemeio/types'
import { toPercent } from '@systemeio/util'

interface Props {
  storeId: StoreId
  item?: PhysicalProductItemViewType
  languageCode: LanguageCodeEnum
}

const Edit: FC<Props> = ({ storeId, item, languageCode }) => {
  const { t } = useLocoTranslation()

  const [tab, setTab] = useState<PhysicalProductTabEnum>(PhysicalProductTabEnum.thumbnail)

  const { data: storeView } = useStore(storeId)

  const defaultValuesWithLoco = {
    ...defaultValues,
    draft: false,
    title: t('dashboard.store.product.physical.title_default', { lng: languageCode }),
    subTitle: t('dashboard.store.product.physical.subtitle_default', { lng: languageCode }),
    buttonTitle: t('dashboard.store.product.physical.button_default', { lng: languageCode }),
    checkoutPage: {
      ...defaultValues.checkoutPage,
      title: t('dashboard.store.product.checkout.title_default', { lng: languageCode }),
      description: t('dashboard.store.product.physical.checkout.subtitle_default_tip-tap', {
        lng: languageCode,
      }),
      buttonTitle: t('dashboard.store.product.checkout.button_default', { lng: languageCode }),
    },
    thankYouPage: {
      title: t('dashboard.store.product.physical.thank_you.title_default', { lng: languageCode }),
      text: t('dashboard.store.product.physical.thank_you.subtitle_default', { lng: languageCode }),
    },
  }

  const [tempState, setTempState] = useState<PhysicalProductItemEditType>(
    item || defaultValuesWithLoco,
  )

  const { errors: initialErrors, setErrors: setEditItemErrors } = useStoreEditItemContext()
  const [errors, setErrors] = useState<PhysicalProductItemErrorsType>({
    ...defaultErrors,
    ...initialErrors,
  })

  useEffect(() => {
    return () => {
      setEditItemErrors({})
    }
  }, [])

  useStoreContactFields({
    savedFields: tempState.checkoutPage.fields,
    initFields: physicalProductInitFields,
    onChange: fields =>
      setTempState(prev => ({ ...prev, checkoutPage: { ...prev.checkoutPage, fields } })),
  })

  const { onPublish, isFetchingPublish, isFetchingDraft, onSaveDraft } = useSavePhysicalProductItem(
    {
      tempState,
      setTempState,
      storeId,
      setErrors,
      initialProductId: item?.checkoutPage.physicalProductId,
    },
  )

  const isNew = !item

  const inReadyToSave = isNew ? true : !!tempState.checkoutPage.physicalProductId

  const isPublishAllowed = tab === PhysicalProductTabEnum.thank_you

  const isCreatingProcess = !item

  const { getTabsWithErrors } = useErrorMessageInTabs()

  const errorsByTab = getTabsWithErrors({ errors })

  const tabs: ItemTabType<PhysicalProductTabEnum>[] = [
    {
      key: PhysicalProductTabEnum.thumbnail,
      caption: t(tabLocoKeys[PhysicalProductTabEnum.thumbnail]),
      hasError: errorsByTab[PhysicalProductTabEnum.thumbnail],
    },
    {
      key: PhysicalProductTabEnum.physical_product,
      caption: t(tabLocoKeys[PhysicalProductTabEnum.physical_product]),
      hasError: errorsByTab[PhysicalProductTabEnum.physical_product],
    },
    {
      key: PhysicalProductTabEnum.checkout,
      caption: t(tabLocoKeys[PhysicalProductTabEnum.checkout]),
      hasError: errorsByTab[PhysicalProductTabEnum.checkout],
    },
    {
      key: PhysicalProductTabEnum.thank_you,
      caption: t(tabLocoKeys[PhysicalProductTabEnum.thank_you]),
      hasError: errorsByTab[PhysicalProductTabEnum.thank_you],
    },
  ]

  const { setPreview, setSingleItemMode } = useStorePreviewContext()
  const { data: physicalProduct } = useProduct(tempState.checkoutPage.physicalProductId)

  const price = !!physicalProduct?.productVariants?.length
    ? physicalProduct?.productVariants?.[0].price ?? cents(0)
    : physicalProduct?.price ?? cents(0)

  const calculatePrice: CalculatePriceInterface | undefined = physicalProduct
    ? {
        originalAmount: price,
        grossAmount: price,
        pricePlan: physicalProduct.pricePlan.id,
        quantity: 1,
        netAmount: price,
        discountedNetAmount: price,
        tax: {
          amount: cents(0),
          rate: toPercent(0),
        },
        discount: {
          amount: cents(0),
          percent: toPercent(0),
        },
        shippingFee: {
          amount: cents(0),
          tax: {
            amount: cents(0),
            rate: toPercent(0),
          },
        },
        currencyCode: physicalProduct.currency,
      }
    : undefined

  useEffect(() => {
    if (
      [PhysicalProductTabEnum.checkout, PhysicalProductTabEnum.physical_product].includes(tab) &&
      physicalProduct
    ) {
      setPreview([
        <PhysicalCheckout
          physicalProduct={physicalProduct}
          calculatePrice={calculatePrice}
          key={'Checkout page preview'}
          checkoutPage={tempState.checkoutPage}
          paymentMethods={storeView?.paymentMethods}
          currency={storeView?.currency}
          thankYouPage={tempState.thankYouPage}
          productType={tempState.productType}
          displayAffiliateLink={false}
          skipDisabledButton={true}
        />,
      ])
      setSingleItemMode(false)
    }
  }, [
    storeView?.currency,
    storeView?.paymentMethods,
    setPreview,
    setSingleItemMode,
    tempState,
    physicalProduct,
    tab,
  ])

  return (
    <div className={twMerge(STORE_ITEM_CLASS_NAME, 'flex flex-col gap-5')}>
      <ItemTabs tabs={tabs} currentTab={tab} setTab={setTab} />
      {tab === PhysicalProductTabEnum.thumbnail && (
        <Thumbnail
          imageType={StoreImageEnum.physical_product_thumbnail}
          tempState={tempState}
          setTempState={setTempState}
          errors={errors}
        />
      )}
      {tab === PhysicalProductTabEnum.checkout && (
        <Checkout
          tempState={tempState}
          setTempState={setTempState}
          errors={errors}
          imageType={StoreImageEnum.physical_product_cover}
        />
      )}
      {tab === PhysicalProductTabEnum.physical_product && storeView?.currency && (
        <PhysicalProduct
          tempState={tempState}
          setTempState={setTempState}
          currency={storeView.currency}
          physicalProductSelectorError={errors['checkoutPage.physicalProductId']}
        />
      )}
      {tab === PhysicalProductTabEnum.thank_you && (
        <ThankYouTab
          thankYouPage={tempState.thankYouPage}
          onChange={thankYouPage => setTempState(prev => ({ ...prev, thankYouPage }))}
          errors={errors}
        />
      )}
      <div className={'flex justify-end gap-5'}>
        {tab === PhysicalProductTabEnum.physical_product &&
          tempState.checkoutPage.physicalProductId && (
            <PrimaryButton
              as={LinkWithoutPrefetch}
              href={`/products/${tempState.checkoutPage.physicalProductId}`}
              target="_blank"
            >
              {t('dashboard.store.item.physical_product.edit')}
            </PrimaryButton>
          )}
        <ItemActions
          publish={{
            disabled: !inReadyToSave,
            onClick: isPublishAllowed || !isCreatingProcess ? onPublish : undefined,
            isFetching: isFetchingPublish,
          }}
          saveDraft={{
            disabled: !inReadyToSave,
            onClick: onSaveDraft,
            isFetching: isFetchingDraft,
          }}
          next={{
            onClick: isCreatingProcess && !isPublishAllowed ? () => setTab(getNextTab) : undefined,
          }}
        />
      </div>
    </div>
  )
}

export default Edit
